// Keystrokes that belong to an input method (Chinese, Japanese, Korean), kept away from the app.
//
// While a word is being composed the keyboard is the IME's: Enter picks a candidate, arrows walk the list,
// Escape cancels. Those same keydowns also reached the app's own handlers, so confirming a word added a
// half-typed tag, renamed a chat to half a word, submitted a search. The chat, Card Forge and Direct Chat
// composers each guarded themselves; the other 64 Enter handlers didn't. One capture-phase listener on the
// window, installed before the app mounts, stops these events before React's root listener or any later
// window listener sees them. Nothing calls preventDefault, so the IME's own commit still happens.

/** Keys WebKit can deliver with keyCode 229 just AFTER a composition ended (see isImeKeydown). */
const COMMIT_KEYS = new Set(["Enter", "Escape", "Tab"]);

/** Does the input method own this keydown? `isComposing` covers the composition itself. WebKit sends the
 *  Enter that commits a conversion with keyCode 229 and `isComposing` already false, so that is matched too —
 *  by key, because Android keyboards send 229 for ordinary typing (as "Unidentified"). */
export function isImeKeydown(e: Pick<KeyboardEvent, "isComposing" | "keyCode" | "key">): boolean {
  return e.isComposing || (e.keyCode === 229 && COMMIT_KEYS.has(e.key));
}

/** Install once per window, before anything else listens for keys. */
export function installImeGuard(target: Window = window): void {
  target.addEventListener("keydown", (e) => { if (isImeKeydown(e)) e.stopImmediatePropagation(); }, true);
}
